/**
 * Licensed Materials - Property of IBM
 * (c) Copyright IBM Corporation 2016, 2023. All Rights Reserved.
 *
 * Note to U.S. Government Users Restricted Rights:
 * Use, duplication or disclosure restricted by GSA ADP Schedule
 * Contract with IBM Corp.
 */
/*global module require*/
"use strict";

function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
function _defineProperty(e, r, t) { return (r = _toPropertyKey(r)) in e ? Object.defineProperty(e, r, { value: t, enumerable: !0, configurable: !0, writable: !0 }) : e[r] = t, e; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == _typeof(i) ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != _typeof(t) || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != _typeof(i)) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
function _typeof(o) { "@babel/helpers - typeof"; return _typeof = "function" == typeof Symbol && "symbol" == typeof Symbol.iterator ? function (o) { return typeof o; } : function (o) { return o && "function" == typeof Symbol && o.constructor === Symbol && o !== Symbol.prototype ? "symbol" : typeof o; }, _typeof(o); }
var clone = require("../../../lib/shared/clone"),
  jsonValidator = require("../../../lib/shared/json-validator"),
  Log = require("../../../lib/client/setup/log"),
  messages = require("../../../nls/setup/deploy/deploy.properties"),
  mustache = require("./mustache"),
  Wizard = require("./wizard"),
  vaultUtil = require("../../../lib/client/vault-util"),
  _ = require("lodash");
module.exports.buildCapabilitiesModel = buildCapabilitiesModel;
module.exports.validateWithSchema = validateWithSchema;
module.exports.addToCapabilitiesModel = addToCapabilitiesModel;
module.exports.applySecretValidator = _applySecretValidator;
var parseTemplate = mustache.parseTemplate,
  getProp = mustache.getProp,
  setProp = mustache.setProp;
var logger = Log.getLogger("model");

// Builds capabilities model that the Integration section can consume
// @param {object} templateData The entire toolchain template model
function buildCapabilitiesModel(originalTemplateData, catalogMap) {
  var _ref = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : {},
    _ref$allowWizard = _ref.allowWizard,
    allowWizard = _ref$allowWizard === void 0 ? true : _ref$allowWizard;
  // Elements inside template will be mutated, so clone to avoid changing the original
  var templateData = clone(originalTemplateData);
  var model = new CapabilityModel(templateData, allowWizard);
  if (catalogMap) {
    model.setCatalog(catalogMap);
  }
  var unresolvedCapabilityIds = {}; // capabilityId -> nil
  var unsupportedCapabilityIds = {}; // capabilityId -> nil
  var isSilentlyAdded = false; // was present in template, not silently added when a field value changed.

  templateData = innerAddToCapabilitiesModel(templateData, model, unresolvedCapabilityIds, unsupportedCapabilityIds, isSilentlyAdded);

  // Toolchain and template are special cases: they participate in the mustache model but aren't services/forms
  var toolchain = templateData.toolchain || {};
  var template = templateData.template || {};
  parseTemplatesInObject(toolchain);
  parseTemplatesInObject(template);
  model.setToolchain(toolchain);
  model.setTemplate(template);
  if (model.isWizardAllowed() && parseInt(templateData.version) >= 3 && template.wizard) {
    // Create the wizard object
    model.setWizard(new Wizard(template.wizard, function () {
      return model;
    }));

    // In wizard mode ensure the original template services match the model services
    originalTemplateData.services = templateData.services;
  }
  model.originalTemplate = clone(originalTemplateData);
  return {
    capabilities: model,
    unresolved: Object.keys(unresolvedCapabilityIds),
    unsupported: Object.keys(unsupportedCapabilityIds)
  };
}

// Mutates this.template from v2 shape into the legacy shape expected by the capabilities model,
// widgets, etc. This involves some stitching together of data
function tweakTemplate(template, catalogMap) {
  var services = template.services;
  var form = template.form || {};
  Object.keys(services).forEach(function (key) {
    return stitchSchema(catalogMap, services, key);
  });
  Object.keys(form).forEach(function (key) {
    return stitchSchema(catalogMap, form, key);
  });
}

// Attach schema to service or form
function stitchSchema(catalog, map, key) {
  var service = map[key];
  var serviceId = service.service_id || key;
  var brokerEntry = catalog[serviceId];
  if (!brokerEntry) {
    return;
  }

  // If this service/form doesn't carry its own schema, stitch in schema from broker entry
  service.service = brokerEntry;
  if (service.schema) {
    service.__schemaOveridden = true;
  } else {
    service.schema = brokerEntry.metadata.parameters;
  }
  if (!service.schema) {
    // Still no schema, provide an empty one
    service.schema = {
      $schema: "http://json-schema.org/draft-04/schema#",
      properties: {},
      required: []
    };
  }
  service.schema.properties = service.schema.properties || {};
  service.parameters = service.parameters || {};
}
function addToCapabilitiesModel(originalTemplate, templateFragment, model, unresolvedCapabilityIds, unsupportedCapabilityIds, isSilentlyAdded) {
  // TODO should probably functionalize this - copy original template & model instead of modifying
  // although, need to modify the original template, or else make a whole new capabilityModel
  var unmodifiedTemplateFragment = templateFragment;

  // Elements inside templateFragment will be mutated, so clone to avoid changing the original
  templateFragment = clone(templateFragment);

  // apply templateFragment.template.required[]
  var requiredCapabilities = model.getTopLevelData().template.required || [];
  var extraRequired = templateFragment.template && templateFragment.template.required ? templateFragment.template.required : [];
  extraRequired.forEach(function (requiredServiceName) {
    requiredCapabilities.push(requiredServiceName);
  });
  innerAddToCapabilitiesModel(templateFragment, model, unresolvedCapabilityIds, unsupportedCapabilityIds, isSilentlyAdded);

  // update the TopLevelData services map
  var servicesMap = model.getTopLevelData().services;
  Object.keys(templateFragment.services || {}).forEach(function (extraServiceName) {
    servicesMap[extraServiceName] = templateFragment.services[extraServiceName];
  });

  // Note, updating the deploy.jsx.state.template object
  var originalTemplateServices = originalTemplate.services;
  Object.keys(unmodifiedTemplateFragment.services || {}).forEach(function (extraServiceName) {
    originalTemplateServices[extraServiceName] = unmodifiedTemplateFragment.services[extraServiceName];
  });
}
function _applySecretValidator(value) {
  if (value && value.parameters && value.schema && value.schema.form && value.schema.properties) {
    // check if at least one password field resides in the schema...
    var _includes_password_field = false;
    var _properties = Object.keys(value.schema.properties) || [];
    for (var i = 0; i < _properties.length; i++) {
      if (_typeof(value.schema.properties[_properties[i]]) === "object" && value.schema.properties[_properties[i]].type === "password") {
        _includes_password_field = true;
        break;
      }
    }
    ;
    // if at least one password field exists, then proceed...
    if (_includes_password_field) {
      var _validator_exists = false;
      var _is_validator_v1_type = false;
      var _form_schema_elements = Object.keys(value.schema.form) || [];
      for (var _i = 0; _i < _form_schema_elements.length; _i++) {
        if (_typeof(value.schema.form[_form_schema_elements[_i]]) === "object" && value.schema.form[_form_schema_elements[_i]].type === "validator") {
          _validator_exists = true;
          if (value.schema.form[_form_schema_elements[_i]].url === "/devops/validator/validator-v1.html") {
            _is_validator_v1_type = true;
          }
          break;
        }
      }
      ;
      // add the validator to the form if not already there...
      if (!_validator_exists) {
        value.schema.form.push({
          type: "validator",
          url: "/devops/validator/validator-v1.html"
        });
        _is_validator_v1_type = true;
      }
      // only attach the secret validator to password fields if no
      // existing validator present and the form is v1 validator type...
      if (_is_validator_v1_type) {
        Object.keys(value.schema.properties).forEach(function (_property) {
          if (_typeof(value.schema.properties[_property]) === "object" && value.schema.properties[_property].type === "password" && !value.schema.properties[_property].validator) {
            value.schema.properties[_property].spinner = "hidden";
            value.schema.properties[_property].validator = {
              "v-type": "secret"
            };
          }
        });
      }
    }
  }
  return value;
}
function innerAddToCapabilitiesModel(templateData, model, unresolvedCapabilityIds, unsupportedCapabilityIds, isSilentlyAdded) {
  var requiredCapabilities = (model.getTopLevelData().template || {}).required || [];
  var serviceData = templateData.services || {};
  var wizardMode = !!(model.isWizardAllowed() && parseInt(templateData.version) >= 3 && templateData.template && templateData.template.wizard);
  var excludedServices = [];
  var excludedLabels = [];
  Object.keys(serviceData).forEach(function (label) {
    // Lookup capability schema from the catalog
    var value = serviceData[label];
    var capabilityId = value.service_id;
    if (!capabilityId) {
      return; // Ignore
    }

    // In wizard mode check is this service excluded
    if (wizardMode && templateData.template.wizard.exclude && templateData.template.wizard.exclude.service_labels && templateData.template.wizard.exclude.service_labels.includes(label)) {
      if (capabilityId === "customtool" && value.parameters && value.parameters.type) {
        capabilityId = capabilityId + "_" + value.parameters.type;
      }
      excludedServices.push(capabilityId);
      excludedLabels.push(label);
      return;
    }
    var schema = value.schema;
    if (!schema) {
      unresolvedCapabilityIds[capabilityId] = true;
      return;
    }
    if (value.service && value.service.provisionable === false) {
      unsupportedCapabilityIds[capabilityId] = true;
    }
    var isRequired = requiredCapabilities.indexOf(label) !== -1;
    value = _applySecretValidator(value);
    model.add(newCapability(value, label, capabilityId, isRequired, isSilentlyAdded));
  });

  // Remove excluded services
  excludedLabels.forEach(function (label) {
    serviceData[label] && delete serviceData[label];
  });

  // Read and mark any forms contributed by the wizard
  var wizardForms = {};
  if (wizardMode && templateData.template.wizard.form) {
    wizardForms = templateData.template.wizard.form;
    Object.keys(wizardForms).forEach(function (serviceId) {
      wizardForms[serviceId].wizard = true;
    });
  }

  // Services have been added. Now deal with form
  var forms = _objectSpread(_objectSpread({}, templateData.form), wizardForms);
  Object.keys(forms).forEach(function (serviceId) {
    if (!serviceId) {
      return; // Ignore
    }
    var form = forms[serviceId];
    var schema = form.schema;
    if (!schema) {
      unresolvedCapabilityIds[serviceId] = true;
      return;
    }
    if (form.service && form.service.provisionable === false) {
      unsupportedCapabilityIds[serviceId] = true;
    }
    // As a form capability, we are considered "required" if some service referencing us is itself required
    var refs = model.getByType(serviceId);
    var isRequired = refs.some(function (capability) {
      return capability.required;
    });
    form = _applySecretValidator(form);
    var formCapability = newCapability(form, serviceId /*label*/, serviceId, isRequired, isSilentlyAdded);

    // Attach this form to each service it's responsible for visualizing
    var attachedToService = false;
    refs.forEach(function (capability) {
      capability.__form = form;
      if (form.metadata.longDescription) {
        capability.metadata.longDescription = form.metadata.longDescription;
      }
      if (form.metadata.description) {
        capability.metadata.description = form.metadata.description;
      }
      attachedToService = true;
    });

    // Add the form capability to the model
    if (wizardMode && excludedServices.includes(serviceId)) {
      if (attachedToService) {
        // Form is attached to another non excluded service
        model.add(formCapability);
      }
    } else {
      model.add(formCapability);
    }
  });
  return templateData;
}

// @returns Capability
function newCapability(data, label, capabilityId, isRequired, isSilentlyAdded) {
  data.label = label;
  if (capabilityId === "customtool") {
    capabilityId = capabilityId + "_" + data.parameters.type;
  }
  data.capabilityId = capabilityId;
  data.required = isRequired;
  var schema = data.schema;
  data.description = schema.description;
  // was silently added when a field value changed, not initially present in template.
  data.silentlyAdded = isSilentlyAdded;
  if (!schema.properties) {
    schema.properties = {};
  }
  data.metadata = data.service ? data.service.metadata : schema;
  // By convention if the schema has these props, they override the catalog values
  ["longDescription", "description"].forEach(function (propName) {
    if (schema[propName]) {
      data.metadata[propName] = schema[propName];
    }
  });
  if (capabilityId.indexOf("customtool") === 0) {
    if (data.metadata && data.parameters.type) {
      data.metadata.displayName = data.parameters.type;
    }
    if (data.metadata && data.parameters.imageUrl) {
      data.metadata.imageUrl = data.parameters.imageUrl;
    }
  }

  // Set initial values of the capability's parameters
  var params = data.parameters;
  if (params) {
    var schemaProps = schema.properties;
    Object.keys(schemaProps).forEach(function (name) {
      var paramDef = schemaProps[name];
      if (_typeof(paramDef) === 'object') {
        if (typeof params[name] !== "undefined") {
          // Template has provided an initial value
          return;
        }
        // Assign default value for the param from the schema
        if (typeof paramDef.default !== "undefined") {
          params[name] = paramDef.default;
        } else {
          var propsEnum = paramDef.enum;
          if (propsEnum && propsEnum.length >= 1) {
            if (_typeof(propsEnum[0]) === "object") {
              params[name] = propsEnum[0].value;
            } else {
              //string
              params[name] = propsEnum[0];
            }
          } else {
            params[name] = zeroValue(paramDef);
          }
        }
        paramDef.spinner = "hidden";
      }
    });
  }

  // For convenience set a boolean on required properties
  (schema.required || []).forEach(function (name) {
    var prop = schema.properties[name];
    if (prop) {
      prop.required = true;
    }
  });
  parseTemplatesInObject(data);
  return data;
}
function locateCreateServiceParametersActions(root) {
  var createServiceParamsActions = [];
  var stack = [{
    path: "",
    obj: root
  }];
  while (stack.length) {
    var next = stack.pop(),
      obj = next.obj,
      path = next.path;
    if (!obj || _typeof(obj) !== "object") {
      continue;
    }
    Object.keys(obj).forEach(function (name) {
      var value = obj[name];
      var newPath = path ? path + "." : "";
      if (name === "type" && value === "create-service") {
        createServiceParamsActions.push(newPath);
        return;
      }
      newPath += name;
      if (value && _typeof(value) === "object") {
        stack.push({
          path: newPath,
          obj: value
        });
      }
    });
  }
  return createServiceParamsActions;
}
function locateUpdateServiceParametersActions(root) {
  var updateServiceParamsActions = [];
  var stack = [{
    path: "",
    obj: root
  }];
  while (stack.length) {
    var next = stack.pop(),
      obj = next.obj,
      path = next.path;
    if (!obj || _typeof(obj) !== "object") {
      continue;
    }
    Object.keys(obj).forEach(function (name) {
      var value = obj[name];
      var newPath = path ? path + "." : "";
      if (name === "type" && value === "update-service-parameters") {
        updateServiceParamsActions.push(newPath);
        return;
      }
      newPath += name;
      if (value && _typeof(value) === "object") {
        stack.push({
          path: newPath,
          obj: value
        });
      }
    });
  }
  return updateServiceParamsActions;
}
function isCreateServiceParametersAction(currentPath, createServiceParamsActionPaths) {
  if (createServiceParamsActionPaths && createServiceParamsActionPaths.length > 0) {
    for (var x = 0; x < createServiceParamsActionPaths.length; x++) {
      if (currentPath.startsWith(createServiceParamsActionPaths[x])) {
        return true;
      }
    }
    ;
  }
  return false;
}
function isUpdateServiceParametersAction(currentPath, updateServiceParamsActionPaths) {
  if (updateServiceParamsActionPaths && updateServiceParamsActionPaths.length > 0) {
    for (var x = 0; x < updateServiceParamsActionPaths.length; x++) {
      if (currentPath.startsWith(updateServiceParamsActionPaths[x])) {
        return true;
      }
    }
    ;
  }
  return false;
}

// Parses template expressions recursively within `root`. Found templates are
// stored in `root.templates` map, keyed by the accessor path of the property
// that they generate.
// Assume no circular references in root since it was originally JSON
function parseTemplatesInObject(root) {
  var updateMode = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : false;
  var createServiceParamsActions = locateCreateServiceParametersActions(root);
  var updateServiceParamsActions = locateUpdateServiceParametersActions(root);
  var templates;
  if (!updateMode) {
    templates = root.templates = Object.create(null);
  } else {
    templates = root.templates;
  }
  var stack = [{
    path: "",
    obj: root
  }];
  while (stack.length) {
    var next = stack.pop(),
      obj = next.obj,
      path = next.path;
    if (!obj || _typeof(obj) !== "object") {
      continue;
    }
    Object.keys(obj).forEach(function (name) {
      var value = obj[name];
      var newPath = (path ? path + "." : "") + name;
      if (!newPath.startsWith("__form")) {
        var template = parseTemplate(value);
        if (template) {
          if (!isUpdateServiceParametersAction(newPath, updateServiceParamsActions) && !isCreateServiceParametersAction(newPath, createServiceParamsActions)) {
            templates[newPath] = template;
          }
          return;
        }
      }
      if (value && _typeof(value) === "object") {
        stack.push({
          path: newPath,
          obj: value
        });
      }
    });
  }
}
function clearTemplatesIfNotMustache(root, parameters) {
  if (root && root.templates && parameters && _typeof(parameters) === "object") {
    var _paths = Object.keys(root.templates);
    if (_paths && _paths.length > 0) {
      var _parameters = listParametersAsPaths(parameters);
      if (_parameters && _parameters.length > 0) {
        _parameters.forEach(function (_param) {
          if (_paths.includes(_param.path)) {
            var _template = root.templates[_param.path];
            if (_template && parseTemplate(_param.value) === null) {
              // ok, found a preexisting mustache template that
              // is now being updated with a non-mustache value
              // therefore need to nuke the preexisting template...
              logger.info("Deleting preexisting mustache template as update-service-parameters value is non-mustache: " + _param.path);
              delete root.templates[_param.path];
            }
          }
        });
      }
    }
  }
}
function listParametersAsPaths(parameters) {
  var paths = [];
  var stack = [{
    path: "parameters",
    obj: parameters
  }];
  while (stack.length) {
    var next = stack.pop(),
      obj = next.obj,
      path = next.path;
    if (!obj || _typeof(obj) !== "object") {
      continue;
    }
    Object.keys(obj).forEach(function (name) {
      var value = obj[name];
      var newPath = (path ? path + "." : "") + name;
      if (value && _typeof(value) === "object") {
        stack.push({
          path: newPath,
          obj: value
        });
      } else {
        paths.push({
          "path": newPath,
          value: value
        });
      }
    });
  }
  return paths;
}
function CapabilityModel(templateData, allowWizard) {
  this.map = Object.create(null); // capabilityId -> Capability[]
  this.metadata = Object.create(null); // capabilityId -> Metadata
  this.label = Object.create(null); // capability.label -> Capability
  this.templateData = templateData; // Handle to the complete template data structure
  this.toolchain = null; // hack
  this.template = null; // hack
  this.catalog = null;
  this.updateStateCallback = null;
  this.allowWizard = !!allowWizard;
}
Object.assign(CapabilityModel.prototype, {
  add: function add(c) {
    var capabilityId = c.capabilityId;
    this.map[capabilityId] = this.map[capabilityId] || [];
    this.map[capabilityId].push(c);
    this.label[c.label] = c;
    if (!c["service-category"]) {
      this.metadata[capabilityId] = c.service ? c.service.metadata : c.schema;
    }
  },
  remove: function remove(service_id, service_instance) {
    if (service_id === "customtool") {
      service_id = service_instance.capabilityId;
    }
    if (this.map && this.map[service_id] && Object.keys(this.map[service_id]).length > 0) {
      var _service_id_instances = this.map[service_id];
      if (_service_id_instances && _service_id_instances.length > 0) {
        var _delete_index;
        var __form;
        for (var x = 0; x < _service_id_instances.length; x++) {
          if (_service_id_instances[x].label === service_instance.label) {
            _delete_index = x;
            if (service_instance.hasOwnProperty("__form")) {
              __form = service_instance.__form;
            }
            break;
          }
        }
        if (_delete_index > -1) {
          _service_id_instances.splice(_delete_index, 1);
          if (__form) {
            _delete_index = undefined;
            for (var _x = 0; _x < _service_id_instances.length; _x++) {
              if (_service_id_instances[_x] === __form) {
                _delete_index = _x;
                break;
              }
            }
            if (_delete_index > -1) {
              _service_id_instances.splice(_delete_index, 1);
            }
          }
          if (this.template && this.template.required && this.template.required.includes(service_instance.label)) {
            _delete_index = this.template.required.indexOf(service_instance.label);
            if (_delete_index > -1) {
              this.template.required.splice(_delete_index, 1);
            }
          }
          if (this.templateData && this.templateData.services[service_instance.label]) {
            delete this.templateData.services[service_instance.label];
          }
          if (this.label && this.label[service_instance.label]) {
            delete this.label[service_instance.label];
            if (__form && this.label[__form.capabilityId]) {
              delete this.label[__form.capabilityId];
            }
          }
          if (_service_id_instances.length <= 1) {
            delete this.map[service_id];
            if (this.metadata && this.metadata[service_id]) {
              delete this.metadata[service_id];
            }
          }
        }
      }
    }
  },
  addToolIntegration: function addToolIntegration(service_id) {
    var _ref2 = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {},
      _ref2$is_update = _ref2.is_update,
      is_update = _ref2$is_update === void 0 ? false : _ref2$is_update,
      _ref2$label = _ref2.label,
      label = _ref2$label === void 0 ? undefined : _ref2$label;
    var parameters = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : {};
    var is_required = arguments.length > 3 && arguments[3] !== undefined ? arguments[3] : true;
    if (!service_id) {
      logger.warn("Cannot add a tool integration without a service_id.");
      return;
    }
    var _integration_name = label;
    if (!_integration_name) {
      var i = 1;
      var _prefix;
      var _services2;
      // if no label provided and it's a secrets type, then autogen a new name...
      // also set this new name on the parameters.name if it's empty...
      if ([vaultUtil.KP_SERVICE_ID, vaultUtil.SM_SERVICE_ID, vaultUtil.HV_SERVICE_ID].includes(service_id)) {
        switch (service_id) {
          case vaultUtil.KP_SERVICE_ID:
            _prefix = "ibm-keyprotect-secrets";
            break;
          case vaultUtil.SM_SERVICE_ID:
            _prefix = "ibm-secrets-manager-secrets";
            break;
          case vaultUtil.HV_SERVICE_ID:
            _prefix = "hashicorp-vault-secrets";
            break;
        }
        _services2 = vaultUtil.getVaultIntegrations(this.getTopLevelData().services);
        while (true) {
          _integration_name = "".concat(_prefix, "-").concat(i);
          if (!_services2.find(function (vi) {
            return vi.name === _integration_name;
          })) {
            break;
          }
          i++;
        }
        if (!parameters.name) {
          parameters.name = _integration_name;
        }
      } else {
        // otherwise, if it's a non-secrets type and no label
        // provided... autogen a new value for the label but
        // do not copy into the parameters.name...
        _prefix = service_id;
        _services2 = this.getTopLevelData().services;
        if (_services2) {
          _services2 = Object.values(_services2);
          while (true) {
            _integration_name = "".concat(_prefix, "-").concat(i);
            if (!_services2.find(function (vi) {
              return vi.label === _integration_name;
            })) {
              break;
            }
            i++;
          }
        } else {
          _integration_name = _prefix + "-1";
        }
      }
    } else if ([vaultUtil.KP_SERVICE_ID, vaultUtil.SM_SERVICE_ID, vaultUtil.HV_SERVICE_ID].includes(service_id)) {
      if (!parameters.name) {
        parameters.name = _integration_name;
      }
    }
    var newServiceTemplateFragment = {
      "template": {},
      "services": {}
    };
    newServiceTemplateFragment.services[_integration_name] = {
      "service_id": service_id,
      "parameters": parameters
    };
    if (is_required === true) {
      newServiceTemplateFragment.template.required = [_integration_name];
    }
    var forms = this.getTopLevelData().form;
    if (forms && Object.keys(forms).length > 0) {
      if (service_id === "customtool") {
        var _customtool_form_name = "customtool_" + _integration_name;
        if (forms[_customtool_form_name]) {
          newServiceTemplateFragment.form = _defineProperty({}, _customtool_form_name, forms[_customtool_form_name]);
        }
      } else {
        if (forms[_integration_name]) {
          newServiceTemplateFragment.form = _defineProperty({}, _integration_name, forms[_integration_name]);
        }
      }
    }

    // During update flow we skip this step, as it stomps over the updated parameters
    if (!is_update) {
      if (this.originalTemplate && this.originalTemplate.services && this.originalTemplate.services[_integration_name] && this.originalTemplate.services[_integration_name].parameters) {
        var _original_parameters = this.originalTemplate.services[_integration_name].parameters;
        if (_typeof(_original_parameters) === "object") {
          newServiceTemplateFragment.services[_integration_name].parameters = _.merge(newServiceTemplateFragment.services[_integration_name].parameters, _original_parameters);
        }
      }
    }
    var unresolvedCapabilityIds = [];
    var unsupportedCapabilityIds = [];
    tweakTemplate(newServiceTemplateFragment, this.getCatalog());
    addToCapabilitiesModel(clone(this.getTopLevelData()), newServiceTemplateFragment, this, /*model*/
    unresolvedCapabilityIds, unsupportedCapabilityIds, true /*isSilentlyAdded*/);

    // bind/rebind any associated form to the new service instance...
    var _services = this.getTopLevelData().services;
    if (_services && _services[_integration_name]) {
      var _service_instance = _services[_integration_name];
      if (_service_instance && _service_instance.hasOwnProperty("__form")) {
        var _forms = this.getTopLevelData().form;
        if (_forms && Object.keys(_forms).length > 0) {
          if (service_id === "customtool") {
            var _customtool_form_name2 = "customtool_" + _integration_name;
            if (_forms[_customtool_form_name2]) {
              _forms[_customtool_form_name2] = _service_instance.__form;
            }
          } else {
            if (_forms[_integration_name]) {
              _forms[_integration_name] = _service_instance.__form;
            }
          }
        }
      }
    }
    if (this.updateStateCallback) {
      this.updateStateCallback({
        capabilities: this
      });
    }
  },
  removeToolIntegration: function removeToolIntegration(service_id) {
    var _ref3 = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {},
      _ref3$name = _ref3.name,
      name = _ref3$name === void 0 ? undefined : _ref3$name,
      _ref3$label = _ref3.label,
      label = _ref3$label === void 0 ? undefined : _ref3$label,
      _ref3$force_delete = _ref3.force_delete,
      force_delete = _ref3$force_delete === void 0 ? false : _ref3$force_delete;
    if (!service_id && !label) {
      logger.warn("Cannot remove a tool integration without a service_id or service_label.");
      return;
    }
    var servicesMap = this.getTopLevelData().services;
    if (servicesMap && Object.keys(servicesMap).length > 0) {
      if (name) {
        logger.info("Removing '" + name + "' parameter named tool integration...");
        var _named_service_instance = undefined;
        Object.keys(servicesMap).forEach(function (_label) {
          if (!_named_service_instance && servicesMap[_label] && servicesMap[_label].parameters && servicesMap[_label].parameters.name === name) {
            _named_service_instance = servicesMap[_label];
          }
        });
        if (_named_service_instance) {
          if (_named_service_instance.required === true && force_delete === false) {
            logger.warn("Cannot remove required '" + name + "' parameter named tool integration.");
          } else {
            this.remove(service_id, _named_service_instance);
          }
        } else {
          logger.info("Did not find '" + name + "' parameter named tool integration - no removal performed.");
        }
      } else if (label) {
        logger.info("Removing '" + label + "' service labelled tool integration...");
        var _labelled_service_instance = servicesMap[label];
        if (_labelled_service_instance) {
          if (_labelled_service_instance.required === true && force_delete === false) {
            logger.warn("Cannot remove required '" + label + "' service labelled tool integration.");
          } else {
            this.remove(_labelled_service_instance.capabilityId, _labelled_service_instance);
          }
        } else {
          logger.info("Did not find '" + label + "' service labelled tool integration - no removal performed.");
        }
      } else {
        logger.info("Removing a tool integration instance of service_id '" + service_id + "'...");
        var _service_id_instance = undefined;
        var _service_names = Object.keys(servicesMap);
        // first pass to find a newly injected integration for removal...
        // therefore leaving integrations originating from the template alone...
        for (var x = 0; x < _service_names.length; x++) {
          var _service_instance = servicesMap[_service_names[x]];
          if (_service_instance && _service_instance.service_id === service_id) {
            if (_service_instance.silentlyAdded === true) {
              _service_id_instance = _service_instance;
              break;
            }
          }
        }
        // if nothing found in the first pass, then try a second
        // pass to find an integration originating from the template...
        if (!_service_id_instance) {
          for (var _x2 = 0; _x2 < _service_names.length; _x2++) {
            var _service_instance2 = servicesMap[_service_names[_x2]];
            if (_service_instance2 && _service_instance2.service_id === service_id) {
              if (_service_instance2.silentlyAdded === false) {
                _service_id_instance = _service_instance2;
                break;
              }
            }
          }
        }
        if (_service_id_instance) {
          if (_service_id_instance.required === true && force_delete === false) {
            logger.warn("Cannot remove a required tool integration of service_id '" + service_id + "'.");
          } else {
            this.remove(service_id, _service_id_instance);
          }
        }
      }
      if (this.updateStateCallback) {
        this.updateStateCallback({
          capabilities: this
        });
      }
    }
  },
  updateToolIntegration: function updateToolIntegration(service_id, service_label, parameters) {
    var _ref4 = arguments.length > 3 && arguments[3] !== undefined ? arguments[3] : {},
      _ref4$keep_service = _ref4.keep_service,
      keep_service = _ref4$keep_service === void 0 ? true : _ref4$keep_service,
      keep_parameters = _ref4.keep_parameters,
      _ref4$allow_forms = _ref4.allow_forms,
      allow_forms = _ref4$allow_forms === void 0 ? false : _ref4$allow_forms;
    var changedParams = [];
    if (!service_id && !service_label) {
      logger.warn("Cannot update tool integration parameters without a service_id or a service_label.");
      return changedParams;
    } else {
      logger.info("Updating parameters on '" + service_label + "' tool integration...");
      var servicesMap = this.getTopLevelData().services;
      var _service_instance = undefined;
      if (servicesMap) {
        _service_instance = servicesMap[service_label];
        if (!_service_instance && allow_forms) {
          _service_instance = this.getByLabel(service_label);
        }
      }
      if (!_service_instance) {
        logger.warn("Did not find '" + service_label + "' tool integration to perform parameters update.");
        return changedParams;
      } else {
        if (!keep_service) {
          var _keep_parameters = {};
          if (keep_parameters) {
            keep_parameters.forEach(function (keepParam) {
              _keep_parameters[keepParam] = _service_instance.parameters[keepParam];
            });
          }
          if (parameters && _typeof(parameters) === "object") {
            _keep_parameters = _.merge(_keep_parameters, parameters);
          }
          // The provided service_id is only for the add step, removal performed based on the service label
          this.removeToolIntegration(null, {
            label: service_label,
            force_delete: true
          });
          this.addToolIntegration(service_id, {
            label: service_label,
            is_update: true
          }, _keep_parameters);
        } else {
          if (parameters && _typeof(parameters) === "object") {
            if (!_service_instance.parameters) {
              _service_instance.parameters = {};
            }

            // Record root level params that will change
            Object.keys(parameters).forEach(function (param) {
              if (!_.isEqual(_service_instance.parameters[param], parameters[param])) {
                changedParams.push(param);
              }
            });
            _service_instance.parameters = _.merge(_service_instance.parameters, parameters);
            clearTemplatesIfNotMustache(_service_instance, parameters);
            parseTemplatesInObject(_service_instance, true /*update mode*/);
          }
        }
        if (this.updateStateCallback) {
          this.updateStateCallback({
            capabilities: this
          });
        }
      }
    }
    return changedParams;
  },
  // Returns capabilities with the given type
  getByType: function getByType(type) {
    return this.map[type] || [];
  },
  // @returns all elements that partake in the mustache engine.
  // This means capabilities and the `toolchain` element (if present)
  __getAll: function __getAll() {
    var _this = this;
    var initial = [this.getToolchain(), this.getTemplate()];
    return this.types().reduce(function (prev, type) {
      return prev.concat(_this.getByType(type));
    }, initial);
  },
  // Returns an element (capability or toolchain) keyed by its label from the template
  getByLabel: function getByLabel(label) {
    return this.label[label] || null;
  },
  // Iterate over each capabilityId and list of capabilities having that id.
  // @param function(type, capabilities, index) fn
  forEachType: function forEachType(fn) {
    var self = this;
    this.types().forEach(function (type, i) {
      fn(type, self.getByType(type), i);
    });
  },
  // Iterate over each capabilityId and list of capabilities having that id.
  // @param function(type, capabilities, index) fn
  forEachLabel: function forEachLabel(fn) {
    var self = this;
    var labels = this.labels();
    labels.forEach(function (type, i) {
      fn(type, self.getByLabel(type), i);
    });
  },
  types: function types() {
    var _this2 = this;
    var _ref5 = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {},
      excludeWizardForms = _ref5.excludeWizardForms;
    if (excludeWizardForms === true) {
      return Object.keys(this.map).filter(function (tool) {
        return !!_this2.map[tool].find(function (cap) {
          return cap.wizard !== true;
        });
      });
    }
    return Object.keys(this.map);
  },
  labels: function labels() {
    var _this3 = this;
    var _ref6 = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {},
      excludeWizardForms = _ref6.excludeWizardForms;
    if (excludeWizardForms === true) {
      return Object.keys(this.label).filter(function (tool) {
        return !!_this3.label[tool].find(function (cap) {
          return cap.wizard !== true;
        });
      });
    }
    return Object.keys(this.label);
  },
  getMetadata: function getMetadata(type) {
    return this.metadata[type] || null;
  },
  // @param {function(variable) -> object} specialExpander
  // @returns array of updated capabilities
  updateAllRefs: function updateAllRefs(specialExpander) {
    var self = this;
    var updated = Object.create(null);
    this.__getAll().forEach(function (capability) {
      Object.keys(capability.templates).forEach(function (paramPath) {
        var template = capability.templates[paramPath];
        var value = template.expand(self, specialExpander);
        var existingValue = getProp(capability, paramPath);
        if (existingValue !== value) {
          // value changed
          setProp(capability, paramPath, value);
          updated[capability.capabilityId] = capability;
        }
        if (value === mustache.unresolvedRef) {
          logger.warn("Failed to expand template at " + paramPath + ": unresolved reference");
        }
      });
    });
    var array = [];
    Object.keys(updated).forEach(function (key) {
      array.push(updated[key]);
    });
    return array;
  },
  deleteTemplate: function deleteTemplate(capability, key, label) {
    var template = capability.templates[key];
    if (!template) {
      return;
    }
    logger.info("[" + (label || capability.label) + "] Deleted template for " + key);
    delete capability.templates[key];
  },
  hasSimpleMustacheTemplate: function hasSimpleMustacheTemplate(capability, key) {
    var template = capability.templates[key];
    if (template) {
      var components = template.components;
      if (components.length === 1) {
        if ('string' !== typeof components[0]) {
          var _components$0$express;
          // non-string component => Variable component
          // camelCase headless variable like inventoryRepo,
          // not multi-part like "services.inventory-repo.parameters.repo_url"
          if (((_components$0$express = components[0].expression) === null || _components$0$express === void 0 ? void 0 : _components$0$express.split('.').length) === 1) {
            return true;
          }
        }
        // Note, looking for a single part with a variable name,
        // to avoid removing the mustache ref for
        // the git param repo_name: 'simple-toolchain-{{timestamp}}'
      }
    }
    return false;
  },
  resolveRef: function resolveRef(specialExpander, capability, paramPath) {
    var template = capability.templates[paramPath];
    if (!template) {
      return undefined;
    }
    var value = template.expand(this, specialExpander);
    if (value === mustache.unresolvedRef) {
      logger.warn("Failed to expand template at " + paramPath + ": unresolved reference");
      return undefined;
    }
    return value;
  },
  setCatalog: function setCatalog(catalog) {
    this.catalog = catalog;
  },
  setToolchain: function setToolchain(toolchain) {
    this.toolchain = toolchain;
    this.label["toolchain"] = toolchain;
  },
  setTemplate: function setTemplate(t) {
    this.template = t;
    this.label["template"] = t;
  },
  setWizard: function setWizard(w) {
    this.wizard = w;
    this.label["wizard"] = w;
  },
  setUpdateStateCallback: function setUpdateStateCallback(cb) {
    this.updateStateCallback = cb;
  },
  isWizardAllowed: function isWizardAllowed() {
    return this.allowWizard;
  },
  getWizard: function getWizard() {
    return this.wizard;
  },
  getCatalog: function getCatalog() {
    return this.catalog;
  },
  getToolchain: function getToolchain() {
    return this.toolchain;
  },
  getTemplate: function getTemplate() {
    return this.template;
  },
  getTopLevelData: function getTopLevelData() {
    return this.templateData;
  },
  setLinkToolchain: function setLinkToolchain(toolchain) {
    this.linkToolchain = toolchain;
  },
  getLinkToolchain: function getLinkToolchain() {
    return this.linkToolchain;
  },
  // Gets the capability ids that are missing configuration.
  // @returns Map<CapabilityId -> string[]> where string[] is a list of invalid fields
  // within the capability
  getConfigRecommended: function getConfigRecommended() {
    var types = Object.create(null);
    this.forEachType(function (type, capabilities) {
      for (var i = 0; i < capabilities.length; i++) {
        var cap = capabilities[i];
        var invalidPropsObj = void 0;
        if (!cap.required) continue;
        invalidPropsObj = validateWithSchema(cap, "setup");
        var noInvalidProps = true;
        if (Object.keys(invalidPropsObj).length !== 0) {
          if (invalidPropsObj.invalidProps.length !== 0) {
            noInvalidProps = false;
          }
        }
        if (isFormValid(cap) && noInvalidProps) continue;
        types[type] = invalidPropsObj;
      }
    });
    return types;
  },
  // Gets the capability ids that are missing configuration for visible wizard steps.
  // @returns Map<CapabilityId -> string[]> where string[] is a list of invalid fields
  // within the capability
  getWizardConfigRecommended: function getWizardConfigRecommended() {
    var invalidCaps = {};
    this.wizard.getSteps({
      visibleOnly: true
    }).forEach(function (step) {
      step.getVisibleCapabilities().forEach(function (cap) {
        var label = cap.label;
        var invalidPropsObj;
        invalidPropsObj = validateWithSchema(cap, "setup");
        var noInvalidProps = true;
        if (Object.keys(invalidPropsObj).length !== 0) {
          if (invalidPropsObj.invalidProps.length !== 0) {
            noInvalidProps = false;
          }
        }
        if (isFormValid(cap) && noInvalidProps) return;
        invalidCaps[label] = invalidPropsObj;
      });
    });
    return invalidCaps;
  },
  // Gets the step names that have invalid configuration for visible wizard steps
  getInvalidWizardSteps: function getInvalidWizardSteps() {
    var invalidSteps = [];
    this.wizard.getSteps({
      visibleOnly: true
    }).forEach(function (step) {
      step.getVisibleCapabilities().forEach(function (cap) {
        var invalidPropsObj;
        invalidPropsObj = validateWithSchema(cap, "setup");
        var noInvalidProps = true;
        if (Object.keys(invalidPropsObj).length !== 0) {
          if (invalidPropsObj.invalidProps.length !== 0) {
            noInvalidProps = false;
          }
        }
        if (isFormValid(cap) && noInvalidProps) return;
        var stepName = step.getName();
        invalidSteps.push(stepName);
      });
    });
    return invalidSteps;
  }
});
function zeroValue(paramDef) {
  switch (paramDef.type) {
    case "array":
      return [];
    case "boolean":
      return false;
    case "number":
      return 0;
    case "null":
      return null;
    // ?
    case "object":
      return {};
    case "string": //fallthrough
    default:
      return "";
  }
}
function isPropHidden(schema, mode, key) {
  var hidden = schema && schema.hidden;
  if (hidden && mode && hidden[mode] && Array.isArray(hidden[mode])) {
    return hidden[mode].includes(key);
  }
  return false;
}

// Returns an array containing the property path(s) that failed validation. Here "validation" means
// capability's current values satisfy the schema and no props have been flagged as invalid via
// a validationMessage field inserted by a remote validator.
// @returns {string[]}
function validateWithSchema(capability, mode) {
  if (capability.schema) {
    var schema = hackEnums(capability.schema);
    var schemaProps = schema.properties || {};
    var values = capability.parameters;
    var valuesToValidate = {};

    //Skip the properties for validation, which don't have any values or are hidden
    for (var val in values) {
      if (values[val] === "" || isPropHidden(schema, mode, val)) {
        continue;
      } else {
        valuesToValidate[val] = values[val];
      }
    }
    var validate = jsonValidator.compile(schema, {
      allErrors: true /* find all errors */
    });
    if (!validate(valuesToValidate)) {
      debugSchemaError(validate.errors, schema, values);
      var invalidProps = validate.errors.map(function (e) {
        return e.path;
      }).filter(function (s) {
        return typeof s === "string";
      });
      var validationMessages = {};
      validate.errors.forEach(function (valError) {
        if (typeof valError.path === "string") {
          if (valError.keyword === "required") {
            validationMessages[valError.path] = messages.get("error.schema.required");
          } else if (valError.keyword === "maxLength") {
            validationMessages[valError.path] = messages.get("error.schema.maxLength");
          } else {
            validationMessages[valError.path] = messages.get("error.schema.validation");
          }
        }
      });
      return {
        "invalidProps": invalidProps,
        "validationMessages": validationMessages
      };
    }

    // Schema passed, now check for validationMessages
    var invalid = [];
    Object.keys(schemaProps).forEach(function (key) {
      if (schemaProps[key].validationMessage) {
        debugValidationMessage(key);
        invalid.push(key);
      }
    });
    return {
      "invalidProps": invalid,
      "validationMessages": {}
    };
  }
  return {};
}
function debugSchemaError(errors, schema, values) {
  if (typeof window !== "undefined") {
    if (window.testFlags && window.testFlags.debugSchema) {
      errors.forEach(function (err) {
        logger.warn("Failed schema validation at `" + err.path + "`, key: `" + err.keyword + "`", "\n values ", values, "\nschema", schema);
      });
    }
  }
}
function debugValidationMessage(key) {
  if (typeof window !== "undefined") {
    if (window.testFlags && window.testFlags.debugSchema) {
      logger.warn("Field was flagged by validator: `" + key + "`");
      return;
    }
  }
}

// Present-day brokers return an intermingling of form + schema data structures. The hybrid
// data structure may contain JSONForm-esque `enum`s in schema elements. These are used
// to render the UI, but the validator interprets them as Schema, leading to spurious
// validation failures.
//
// @returns A copy of the Schema with problematic enums removed.
//
// TODO brokers should return schema & form *separately* which would remove the need for this
function hackEnums(schema, capability) {
  var form = schema.form;
  if (!form) {
    return schema;
  }

  // clone schema since it may be mutated
  schema = JSON.parse(JSON.stringify(schema));
  var formMap = Object.create(null); // Map<prop name -> form element>
  findFormElements(formMap, form);
  var typesMap = Object.create(null);
  findTypesWithEnum(typesMap, schema); // Map<prop name -> JSON schema>

  Object.keys(typesMap).forEach(function (key) {
    var schemaElement = typesMap[key];
    if (!Array.isArray(schemaElement.enum)) {
      return; // should not happen
    }

    // If enum elements have the Form shape { name, value} throw it in the trash
    var first = schemaElement.enum[0];
    if (first && typeof first.name === "string" && typeof first.value === "string") {
      delete schemaElement.enum;
    }

    // If the field is visualized as a combo box (ie. a select that's not readonly), this also
    // tends to mean that the enum is bad and should not be used for validation.
    var formElement = formMap[key];
    if (!formElement || formElement.type !== "select") {
      return;
    }
    if (formElement.readonly === false) {
      // combo box
      delete schemaElement.enum;
    } else {
      // selection dropdown. should we do something here?
    }
  });
  return schema;
}
function findFormElements(map, obj) {
  for (var prop in obj) {
    var value = obj[prop];
    if (prop === "key" && typeof value === "string") {
      map[value] = obj;
    } else if (value && _typeof(value) === "object") {
      findFormElements(map, value);
    }
  }
}

// Finds types with enums in schema `obj`
function findTypesWithEnum(map, obj) {
  for (var key in obj) {
    var value = obj[key];
    if (value && _typeof(value) === "object") {
      if (Array.isArray(value.enum)) {
        map[key] = value;
      }
      findTypesWithEnum(map, value);
    }
  }
}

// Returns whether this capability's form validator has deemed it "valid".
function isFormValid(capability) {
  var valid = capability.isFormValid;
  return typeof valid === "undefined" ? true : !!valid;
}